import { useEffect, useState } from 'react';
import useCalculatorService from './services/useCalculatorService';

const toInputDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Safer: build a local-date Date object (no hardcoded T22:59…)
const fromInputDate = (yyyyMmDd) => new Date(`${yyyyMmDd}T22:59:59.999Z`);

const AccountsView = () => {
  const {
    getAccountModels,
    updateAccountModel,
    addAccountModel,
  } = useCalculatorService();

  const [fromDate, setFromDate] = useState(() => new Date());

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // edit state (row editing)
  const [editingId, setEditingId] = useState(null);
  const [editingModel, setEditingModel] = useState({
    name: '',
    amount: '',
    dateFrom: '',
    dateTo: '',
  });

  // add state (new account)
  const [newModel, setNewModel] = useState({
    name: '',
    amount: '',
    dateFrom: toInputDate(new Date()),
    dateTo: '',
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAccountModels(fromDate);
      setAccounts(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message ?? 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [fromDate]);

  const startEdit = (account) => {
    setEditingId(account.id);
    setEditingModel({
      name: String(account.name ?? ''),
      amount: String(account.amount ?? ''),
      dateFrom: account.dateFrom ? toInputDate(account.dateFrom) : '',
      dateTo: account.dateTo ? toInputDate(account.dateTo) : '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingModel({ name: '', amount: '', dateFrom: '', dateTo: '' });
  };

  const saveEdit = async (id) => {
    setError('');
    setSaving(true);
    try {
      const amountNum = Number(editingModel.amount);
      if (Number.isNaN(amountNum)) throw new Error('Amount must be a number');

      // If your PATCH endpoint ONLY supports {id, amount}, keep only that:
      const res = await updateAccountModel({
        id,
        amount: amountNum,
        // If backend supports it, you could extend updateAccountModel to accept these:
        // name: editingModel.name,
        // dateFrom: editingModel.dateFrom ? fromInputDate(editingModel.dateFrom) : null,
        // dateTo: editingModel.dateTo ? fromInputDate(editingModel.dateTo) : null,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Update failed');
      }

      // Optimistic update (amount + optionally other fields)
      setAccounts((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                amount: amountNum,
                name: editingModel.name,
                dateFrom: editingModel.dateFrom ? fromInputDate(editingModel.dateFrom) : a.dateFrom,
                dateTo: editingModel.dateTo ? fromInputDate(editingModel.dateTo) : a.dateTo,
              }
            : a
        )
      );

      cancelEdit();
    } catch (e) {
      setError(e?.message ?? 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const addNew = async () => {
    setError('');
    setSaving(true);
    try {
      const amountNum = Number(newModel.amount);
      if (Number.isNaN(amountNum)) throw new Error('Amount must be a number');
      if (!newModel.name.trim()) throw new Error('Name is required');

      const payload = {
        name: newModel.name.trim(),
        amount: amountNum,
        dateFrom: newModel.dateFrom ? fromInputDate(newModel.dateFrom) : null,
        dateTo: newModel.dateTo ? fromInputDate(newModel.dateTo) : null,
      };

      const res = await addAccountModel(payload);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Create failed');
      }

      // simplest: reload from server so ids/dates are consistent
      await load();

      setNewModel({
        name: '',
        amount: '',
        dateFrom: toInputDate(new Date()),
        dateTo: '',
      });
    } catch (e) {
      setError(e?.message ?? 'Failed to add account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 950 }}>
      <h2>Accounts</h2>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
        <div>
          <label>Ընտրել հաշիվների օրը</label>
          <br />
          <input
            type="date"
            value={toInputDate(fromDate)}
            onChange={(e) => setFromDate(fromInputDate(e.target.value))}
          />
        </div>

        <button onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Թարմացնել'}
        </button>
      </div>

      {error ? <p style={{ color: 'crimson', marginTop: 12 }}>{error}</p> : null}

      <h3 style={{ marginTop: 20 }}>Ավելացնել հաշիվ</h3>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
        <div>
          <label>Անուն</label>
          <br />
          <input
            value={newModel.name}
            onChange={(e) => setNewModel((p) => ({ ...p, name: e.target.value }))}
            style={{ width: 220 }}
          />
        </div>

        <div>
          <label>Քանակ</label>
          <br />
          <input
            value={newModel.amount}
            onChange={(e) => setNewModel((p) => ({ ...p, amount: e.target.value }))}
            inputMode="decimal"
            style={{ width: 120 }}
          />
        </div>

        <div>
          <label>Ամսաթիվ սկսած</label>
          <br />
          <input
            type="date"
            value={newModel.dateFrom}
            onChange={(e) => setNewModel((p) => ({ ...p, dateFrom: e.target.value }))}
          />
        </div>

        <div>
          <label>Ամսաթիվ մինչև</label>
          <br />
          <input
            type="date"
            value={newModel.dateTo}
            onChange={(e) => setNewModel((p) => ({ ...p, dateTo: e.target.value }))}
          />
        </div>

        <button onClick={addNew} disabled={saving}>
          {saving ? 'Saving…' : 'Ավելացնել'}
        </button>
      </div>

      <h3 style={{ marginTop: 20 }}>Հաշիվներ</h3>

      <table width="100%" cellPadding="8" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left' }}>
            <th style={{ borderBottom: '1px solid #ddd' }}>Անուն</th>
            <th style={{ borderBottom: '1px solid #ddd' }}>Քանակ</th>
            <th style={{ borderBottom: '1px solid #ddd' }}>Ամսաթիվ սկսած</th>
            <th style={{ borderBottom: '1px solid #ddd' }}>Ամսաթիվ մինչև</th>
            <th style={{ borderBottom: '1px solid #ddd' }} />
          </tr>
        </thead>

        <tbody>
          {accounts.map((a) => {
            const isEditing = editingId === a.id;

            return (
              <tr key={a.id}>
                <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {isEditing ? (
                    <input
                      value={editingModel.name}
                      onChange={(e) => setEditingModel((p) => ({ ...p, name: e.target.value }))}
                      style={{ width: 220 }}
                    />
                  ) : (
                    a.name
                  )}
                </td>

                <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {isEditing ? (
                    <input
                      value={editingModel.amount}
                      onChange={(e) => setEditingModel((p) => ({ ...p, amount: e.target.value }))}
                      inputMode="decimal"
                      style={{ width: 120 }}
                    />
                  ) : (
                    a.amount
                  )}
                </td>

                <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editingModel.dateFrom}
                      onChange={(e) => setEditingModel((p) => ({ ...p, dateFrom: e.target.value }))}
                    />
                  ) : (
                    a.dateFrom ? new Date(a.dateFrom).toLocaleDateString() : ''
                  )}
                </td>

                <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editingModel.dateTo}
                      onChange={(e) => setEditingModel((p) => ({ ...p, dateTo: e.target.value }))}
                    />
                  ) : (
                    a.dateTo ? new Date(a.dateTo).toLocaleDateString() : ''
                  )}
                </td>

                <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => saveEdit(a.id)} disabled={saving}>
                        Պահպանել
                      </button>
                      <button onClick={cancelEdit} disabled={saving}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => startEdit(a)}>Փոխել</button>
                  )}
                </td>
              </tr>
            );
          })}

          {!accounts.length && !loading ? (
            <tr>
              <td colSpan={5} style={{ padding: 16, color: '#666' }}>
                Տվյալ ամսաթվի համար Հաշիվներ չկան
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

export default AccountsView;